import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

// =====================================================
// TYPES
// =====================================================

export interface StaffingLimitBase {
  id: string
  tenant_id: string
  organization_id: string
  position_id: string
  department_id: string | null
  created_at: string
  updated_at: string
  organization?: {
    id: string
    name: string
  }
  position?: {
    id: string
    name: string
  }
  department?: {
    id: string
    name: string
  } | null
}

export interface DailyStaffingLimit extends StaffingLimitBase {
  type: 'daily'
  date: string
  max_employees: number
}

export interface MonthlyStaffingLimit extends StaffingLimitBase {
  type: 'monthly'
  year: number
  month: number
  max_hours: number
  max_shifts: number | null
  source: 'manual' | '1c_staffing'
}

// Legacy type for backward compatibility
export interface StaffingLimit {
  id: string
  tenant_id: string
  date: string
  position_id: string
  department_id: string | null
  max_count: number
  organization_id?: string
  calculated_at?: string
  created_at: string
  updated_at: string
  position?: {
    id: string
    name: string
  }
  department?: {
    id: string
    name: string
  } | null
}

// =====================================================
// FILTER TYPES
// =====================================================

export interface DailyStaffingLimitFilters {
  organizationId?: string
  dateFrom?: string
  dateTo?: string
  positionId?: string
  departmentId?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
}

export interface MonthlyStaffingLimitFilters {
  organizationId?: string
  year?: number
  month?: number
  positionId?: string
  departmentId?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
}

// Legacy filter type
export interface StaffingLimitFilters {
  type?: 'daily' | 'monthly'
  organizationId?: string
  search?: string
  positionId?: string
  departmentId?: string
  dateFrom?: string
  dateTo?: string
  year?: number
  month?: number
  page?: number
  limit?: number
  sort?: string
}

// =====================================================
// PAGINATED RESPONSE TYPES
// =====================================================

export interface PaginatedDailyLimits {
  data: DailyStaffingLimit[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface PaginatedMonthlyLimits {
  data: MonthlyStaffingLimit[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface PaginatedStaffingLimits {
  data: StaffingLimit[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// =====================================================
// CREATE/UPDATE DATA TYPES
// =====================================================

export interface CreateDailyStaffingLimitData {
  organization_id: string
  position_id: string
  department_id?: string | null
  date: string
  max_employees: number
}

export interface UpdateDailyStaffingLimitData {
  organization_id?: string
  position_id?: string
  department_id?: string | null
  date?: string
  max_employees?: number
}

export interface CreateMonthlyStaffingLimitData {
  organization_id: string
  position_id: string
  department_id?: string | null
  year: number
  month: number
  max_hours?: number
  max_shifts?: number
}

export interface UpdateMonthlyStaffingLimitData {
  organization_id?: string
  position_id?: string
  department_id?: string | null
  year?: number
  month?: number
  max_hours?: number
  max_shifts?: number
}

// Legacy types for backward compatibility
export interface CreateStaffingLimitData {
  date: string
  position_id: string
  department_id?: string | null
  organization_id?: string
  max_count: number
}

export interface UpdateStaffingLimitData {
  date?: string
  position_id?: string
  department_id?: string | null
  organization_id?: string
  max_count?: number
}

// =====================================================
// CHECK RESULT TYPES
// =====================================================

export interface DailyLimitCheckResult {
  hasLimit: boolean
  maxEmployees: number
  currentScheduled: number
  available: number
  exceeded: boolean
}

export interface MonthlyLimitCheckResult {
  hasLimit: boolean
  maxHours: number
  currentHours: number
  projectedHours: number
  available: number
  exceeded: boolean
}

// =====================================================
// QUERY KEYS
// =====================================================

const STAFFING_LIMITS_QUERY_KEY = 'staffing-limits'
const DAILY_LIMITS_QUERY_KEY = 'daily-staffing-limits'
const MONTHLY_LIMITS_QUERY_KEY = 'monthly-staffing-limits'

// =====================================================
// DAILY LIMITS HOOKS
// =====================================================

export const useDailyStaffingLimits = (filters: DailyStaffingLimitFilters = {}) => {
  return useQuery({
    queryKey: [DAILY_LIMITS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedDailyLimits> => {
      const response = await apiClient.getDailyStaffingLimits(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
    retry: 3,
  })
}

export const useCreateDailyStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateDailyStaffingLimitData) => {
      const response = await apiClient.createDailyStaffingLimit(data)
      return response.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DAILY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
    },
  })
}

export const useUpdateDailyStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDailyStaffingLimitData }) => {
      const response = await apiClient.updateDailyStaffingLimit(id, data)
      return response.data || response
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [DAILY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY, id] })
    },
  })
}

export const useCheckDailyLimit = (params: {
  date: string
  positionId: string
  departmentId?: string
  organizationId?: string
}) => {
  return useQuery({
    queryKey: ['check-daily-limit', params],
    queryFn: async (): Promise<DailyLimitCheckResult> => {
      return apiClient.checkDailyStaffingLimit(params)
    },
    enabled: !!params.date && !!params.positionId,
    staleTime: 10 * 1000,
  })
}

// =====================================================
// MONTHLY LIMITS HOOKS
// =====================================================

export const useMonthlyStaffingLimits = (filters: MonthlyStaffingLimitFilters = {}) => {
  return useQuery({
    queryKey: [MONTHLY_LIMITS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedMonthlyLimits> => {
      const response = await apiClient.getMonthlyStaffingLimits(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
    retry: 3,
  })
}

export const useCreateMonthlyStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateMonthlyStaffingLimitData) => {
      const response = await apiClient.createMonthlyStaffingLimit(data)
      return response.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MONTHLY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
    },
  })
}

export const useUpdateMonthlyStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMonthlyStaffingLimitData }) => {
      const response = await apiClient.updateMonthlyStaffingLimit(id, data)
      return response.data || response
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [MONTHLY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['shift-limits'] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY, id] })
    },
  })
}

export const useCheckMonthlyLimit = (params: {
  year: number
  month: number
  positionId: string
  departmentId?: string
  organizationId?: string
  additionalMinutes?: number
}) => {
  return useQuery({
    queryKey: ['check-monthly-limit', params],
    queryFn: async (): Promise<MonthlyLimitCheckResult> => {
      return apiClient.checkMonthlyStaffingLimit(params)
    },
    enabled: !!params.year && !!params.month && !!params.positionId,
    staleTime: 10 * 1000,
  })
}

// =====================================================
// GENERATE MONTHLY LIMITS TYPES & HOOKS
// =====================================================

export interface PositionLimitPreview {
  position_id: string
  position_name: string
  employee_count: number
  calculated_hours: number
  existing_limit_id?: string
  existing_max_hours?: number
  suggested_max_hours: number
}

export interface PreviewMonthlyLimitsResponse {
  organization_id: string
  organization_name: string
  year: number
  month: number
  positions: PositionLimitPreview[]
  total_positions: number
  total_calculated_hours: number
  limits_to_create: number
  limits_to_update: number
}

export interface GenerateMonthlyLimitsData {
  organization_id: string
  year: number
  month: number
  limits: Array<{ position_id: string; max_hours: number }>
}

export interface GenerateMonthlyLimitsResponse {
  created: number
  updated: number
  total: number
  limits: MonthlyStaffingLimit[]
}

const PREVIEW_LIMITS_QUERY_KEY = 'preview-monthly-limits'

export const usePreviewMonthlyLimits = (
  params: { organization_id: string; year: number; month: number },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [PREVIEW_LIMITS_QUERY_KEY, params],
    queryFn: async (): Promise<PreviewMonthlyLimitsResponse> => {
      return apiClient.previewMonthlyStaffingLimits(params)
    },
    enabled:
      options?.enabled !== false &&
      !!params.organization_id &&
      !!params.year &&
      !!params.month,
    staleTime: 30 * 1000,
  })
}

export const useGenerateMonthlyLimits = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      data: GenerateMonthlyLimitsData
    ): Promise<GenerateMonthlyLimitsResponse> => {
      return apiClient.generateMonthlyStaffingLimits(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MONTHLY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [PREVIEW_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
    },
  })
}

// =====================================================
// SHIFT LIMITS FROM 1C STAFFING SCHEDULE
// =====================================================

export interface GenerateShiftLimitsData {
  year: number
  month: number
  overwrite_manual?: boolean
}

export interface ShiftLimitGenerationItem {
  position_id: string
  position_name: string
  department_id: string
  department_name: string
  units: number
  calculated_shifts: number
  max_shifts: number
  action: 'created' | 'updated' | 'skipped_manual' | 'skipped_zero'
  units_without_calendar: number
}

export interface GenerateShiftLimitsResponse {
  year: number
  month: number
  created: number
  updated: number
  skipped_manual: number
  skipped_zero: number
  skipped_units: {
    total_active: number
    no_position: number
    no_department: number
    no_template: number
    no_organization: number
  }
  items: ShiftLimitGenerationItem[]
}

export interface ShiftLimit {
  id: string
  year: number
  month: number
  max_shifts: number
  used_shifts: number | null
  available: number | null
  exceeded: boolean
  employees_count: number | null
  source: 'manual' | '1c_staffing'
  organization: { id: string; name: string } | null
  position: { id: string; name: string } | null
  department: { id: string; name: string } | null
  updated_at: string
}

export interface ShiftLimitFilters {
  organizationId?: string
  departmentId?: string
  positionId?: string
  year?: number
  month?: number
  search?: string
  with_usage?: boolean
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedShiftLimits {
  data: ShiftLimit[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const SHIFT_LIMITS_QUERY_KEY = 'shift-limits'

export const useShiftLimits = (filters: ShiftLimitFilters = {}) => {
  return useQuery({
    queryKey: [SHIFT_LIMITS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedShiftLimits> => {
      const response = await apiClient.getShiftLimits(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
    retry: 1,
  })
}

export const useGenerateShiftLimits = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      data: GenerateShiftLimitsData
    ): Promise<GenerateShiftLimitsResponse> => {
      return apiClient.generateShiftLimitsFromStaffing(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MONTHLY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [SHIFT_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
    },
  })
}

// =====================================================
// LEGACY HOOKS (for backward compatibility)
// =====================================================

export const useStaffingLimits = (filters: StaffingLimitFilters = {}) => {
  return useQuery({
    queryKey: [STAFFING_LIMITS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedStaffingLimits> => {
      const response = await apiClient.getStaffingLimits(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
    retry: 3,
  })
}

export const useStaffingLimit = (id: string) => {
  return useQuery({
    queryKey: [STAFFING_LIMITS_QUERY_KEY, id],
    queryFn: async (): Promise<StaffingLimit> => {
      const response = await apiClient.getStaffingLimit(id)
      return response.data || response
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateStaffingLimitData) => {
      const response = await apiClient.createStaffingLimit(data)
      return response.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DAILY_LIMITS_QUERY_KEY] })
    },
  })
}

export const useUpdateStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateStaffingLimitData }) => {
      const response = await apiClient.updateStaffingLimit(id, data)
      return response.data || response
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DAILY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY, id] })
    },
  })
}

export const useDeleteStaffingLimit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deleteStaffingLimit(id)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STAFFING_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DAILY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [MONTHLY_LIMITS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['shift-limits'] })
    },
  })
}
